import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AUTH } from "@/constants/testIds";
import { client } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { KILI } from "@/lib/design";

// Member sign-in gate. Google is enabled today via Emergent Auth.
// Phone (WhatsApp/SMS OTP) and Telegram Login Widget appear as "coming soon"
// until Twilio + BotFather credentials arrive.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Join() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    // If already signed in, jump straight to the intended destination.
    if (!loading && user) {
      navigate(next, { replace: true });
    }
  }, [loading, user, next, navigate]);

  useEffect(() => {
    client.get("/auth/providers").then((r) => setProviders(r.data.providers || [])).catch(() => {});
  }, []);

  const startGoogle = () => {
    // Remember where the user was headed so AuthCallback can bounce them back.
    sessionStorage.setItem("watuwako:next", next);
    sessionStorage.setItem("watuwako:mode", "member");
    const redirectUrl = window.location.origin + "/join/return";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="theme-night min-h-screen flex items-center justify-center" style={{ background: "var(--bg-night)", color: "var(--text-night)" }}>
      <div className="w-full max-w-md rounded-2xl border p-10" style={{ borderColor: "var(--line-night)", background: "var(--card-night)" }}>
        <div className="flex items-center gap-3">
          <span className="compass-mark" />
          <span className="wordmark text-xl">watu wako</span>
        </div>
        <p className="eyebrow mt-8" style={{ color: "var(--text-muted-night)" }}>watu wako</p>
        <h1 className="font-display mt-2 text-3xl">Create a free account to submit</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted-night)" }}>
          Free, takes a few seconds. You'll use it to post events and — soon — build squads and rate what you go to.
        </p>

        <div className="mt-8 space-y-3">
          <button
            data-testid={AUTH.memberGoogleButton}
            onClick={startGoogle}
            className="flex w-full items-center justify-center gap-3 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: "#FFFFFF", color: "#1A1A30" }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <button
            data-testid={AUTH.memberPhoneButton}
            disabled
            className="flex w-full items-center justify-center gap-3 rounded-full border px-6 py-3 text-sm font-semibold opacity-60 cursor-not-allowed"
            style={{ borderColor: "var(--line-night)", color: "var(--text-muted-night)" }}
            title="Waiting on Twilio WhatsApp Business sender approval"
          >
            Continue with phone <span className="text-[10px] uppercase tracking-widest ml-1">coming soon</span>
          </button>

          <button
            data-testid={AUTH.memberTelegramButton}
            disabled
            className="flex w-full items-center justify-center gap-3 rounded-full border px-6 py-3 text-sm font-semibold opacity-60 cursor-not-allowed"
            style={{ borderColor: "var(--line-night)", color: "var(--text-muted-night)" }}
            title="Waiting on Telegram bot username + Login Widget domain registration"
          >
            Continue with Telegram <span className="text-[10px] uppercase tracking-widest ml-1">coming soon</span>
          </button>
        </div>

        <p className="mt-6 text-xs" style={{ color: "var(--text-dim-night)" }}>
          We only use this to keep events honest. No spam, ever.
        </p>

        <p className="mt-6 text-xs" style={{ color: "var(--text-dim-night)" }}>
          <Link to="/app" className="underline">Just browsing?</Link> No account needed.
        </p>

        {providers.length > 0 && (
          <details className="mt-6 text-xs opacity-60">
            <summary>Provider status</summary>
            <ul className="mt-2 space-y-1">
              {providers.map((p) => (
                <li key={p.key}>{p.label} — <span style={{ color: p.status === "enabled" ? KILI : "var(--text-dim-night)" }}>{p.status}</span></li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
